import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../../components/AdminLayout';
import { analyticsAPI, ordersAPI } from '../../lib/api';
import { useApiQuery } from '../../hooks/useApiQuery';
import { 
  Package, 
  Layers, 
  ShoppingCart, 
  DollarSign, 
  Clock, 
  CheckCircle, 
  XCircle,
  TrendingUp,
  AlertTriangle,
  ArrowRight,
  Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { DataTable } from '../../components/DataTable';

const AdminDashboard = () => {
  const { data: statsRes, isLoading: loadingStats } = useApiQuery(
    ['analyticsStats'],
    () => analyticsAPI.getStats()
  );

  const { data: allOrdersRes, isLoading: loadingOrders } = useApiQuery(
    ['allOrders'],
    () => ordersAPI.getAll()
  );

  const loading = loadingStats || loadingOrders;
  const stats = statsRes?.data || null;
  const allOrders = allOrdersRes?.data || [];
  
  // Get recent 5 orders instead of a separate API call
  const orders = [...allOrders]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 5);

  const orderStats = {
    total: allOrders.length,
    pending: allOrders.filter(o => o.status === 'pending').length,
    processing: allOrders.filter(o => o.status === 'processing').length,
    delivered: allOrders.filter(o => o.status === 'delivered').length,
    cancelled: allOrders.filter(o => o.status === 'cancelled').length,
    revenue: allOrders
      .filter(o => o.status !== 'cancelled')
      .reduce((sum, o) => sum + (o.total_amount || 0), 0)
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      processing: { color: 'bg-blue-100 text-blue-800', icon: Package },
      shipped: { color: 'bg-purple-100 text-purple-800', icon: TrendingUp },
      delivered: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle },
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <div className="h-8 bg-slate-200 rounded skeleton w-48" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-slate-200 rounded-xl skeleton" />
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-medium tracking-tight mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Dashboard
            </h1>
            <p className="text-slate-600">Welcome back! Here's an overview of your store.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/admin/products">
              <Button variant="outline" className="gap-2">
                <Package className="w-4 h-4" />
                View Products
              </Button>
            </Link>
            <Link to="/admin/orders">
              <Button className="gap-2 bg-[#8acb95] hover:bg-[#7bb885] text-white">
                <ShoppingCart className="w-4 h-4" />
                View Orders
              </Button>
            </Link>
          </div>
        </div>

        {/* Order Stats */}
        <div>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <ShoppingCart className="w-5 h-5 text-[#8acb95]" />
            Order Statistics
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card className="bg-gradient-to-br from-slate-50 to-white border-slate-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Orders</p>
                    <p className="text-2xl font-bold mt-1">{orderStats.total}</p>
                  </div>
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                    <ShoppingCart className="w-5 h-5 text-slate-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-yellow-50 to-white border-yellow-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-yellow-600 uppercase tracking-wider">Pending</p>
                    <p className="text-2xl font-bold mt-1 text-yellow-700">{orderStats.pending}</p>
                  </div>
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-yellow-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-blue-600 uppercase tracking-wider">Processing</p>
                    <p className="text-2xl font-bold mt-1 text-blue-700">{orderStats.processing}</p>
                  </div>
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Package className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-green-600 uppercase tracking-wider">Delivered</p>
                    <p className="text-2xl font-bold mt-1 text-green-700">{orderStats.delivered}</p>
                  </div>
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-red-50 to-white border-red-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-red-600 uppercase tracking-wider">Cancelled</p>
                    <p className="text-2xl font-bold mt-1 text-red-700">{orderStats.cancelled}</p>
                  </div>
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <XCircle className="w-5 h-5 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-emerald-600 uppercase tracking-wider">Revenue</p>
                    <p className="text-xl font-bold mt-1 text-emerald-700">KWD {orderStats.revenue.toFixed(3)}</p>
                  </div>
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Store Stats */}
        <div>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Package className="w-5 h-5 text-[#8acb95]" />
            Store Overview
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card data-testid="stat-total-products">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Products</CardTitle>
                <Package className="w-4 h-4 text-[#8acb95]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats?.total_products || 0}</div>
                <p className="text-xs text-slate-600 mt-1">Active listings in catalog</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-total-categories">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Categories</CardTitle>
                <Layers className="w-4 h-4 text-[#8acb95]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats?.total_categories || 0}</div>
                <p className="text-xs text-slate-600 mt-1">Product categories</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-total-visitors">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Page Views</CardTitle>
                <Eye className="w-4 h-4 text-[#8acb95]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats?.total_visitors || 0}</div>
                <p className="text-xs text-slate-600 mt-1">Total page views</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-low-stock">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Low Stock Alert</CardTitle>
                <AlertTriangle className="w-4 h-4 text-orange-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">{stats?.low_stock_products || 0}</div>
                <p className="text-xs text-slate-600 mt-1">Items below 5 units</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Recent Orders */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Clock className="w-5 h-5 text-[#8acb95]" />
              Recent Orders
            </h2>
            <Link to="/admin/orders">
              <Button variant="ghost" size="sm" className="gap-1 text-[#8acb95] hover:text-[#7bb885]">
                View All
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
          
          <div className="mt-2 text-left">
            <DataTable 
              columns={[
                {
                  accessorKey: 'order_number',
                  header: 'Order #',
                  cell: ({ row }) => <span className="font-mono text-sm font-medium text-[#8acb95]">{row.original.order_number}</span>
                },
                {
                  accessorKey: 'customer_name',
                  header: 'Customer',
                  cell: ({ row }) => (
                    <div>
                      <p className="font-medium text-sm">{row.original.customer_name}</p>
                      <p className="text-xs text-slate-500">{row.original.customer_email}</p>
                    </div>
                  )
                },
                {
                  id: 'items',
                  header: 'Items',
                  cell: ({ row }) => <span className="text-sm">{row.original.items?.length || 0} items</span>
                },
                {
                  accessorKey: 'total_amount',
                  header: 'Total',
                  cell: ({ row }) => <span className="font-semibold text-sm">KWD {row.original.total_amount?.toFixed(3)}</span>
                },
                {
                  accessorKey: 'status',
                  header: 'Status',
                  cell: ({ row }) => getStatusBadge(row.original.status)
                },
                {
                  accessorKey: 'created_at',
                  header: 'Date',
                  cell: ({ row }) => <span className="text-sm text-slate-600">{new Date(row.original.created_at).toLocaleDateString()}</span>
                }
              ]}
              data={orders}
              isLoading={loadingOrders}
              emptyMessage="No orders yet"
            />
          </div>
        </div>

        {/* Most Viewed Products */}
        {stats?.most_viewed_products && stats.most_viewed_products.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <TrendingUp className="w-5 h-5 text-[#8acb95]" />
              Most Viewed Products
            </h2>
            <Card>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {stats.most_viewed_products.map((item, index) => (
                    <div key={item._id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                          index === 0 ? 'bg-yellow-500' : 
                          index === 1 ? 'bg-slate-400' : 
                          index === 2 ? 'bg-amber-600' : 'bg-slate-300'
                        }`}>
                          {index + 1}
                        </div>
                        <span className="font-medium">{item.product_name || 'Unknown Product'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-slate-400" />
                        <span className="font-semibold text-[#8acb95]">{item.views} views</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
